<template>
  <div class="tw:flex tw:flex-col tw:gap-y-6">
    <StudioPlusReactivationHeader
      v-if="!loading"
      :type="type"
      :isExpired="defaultPaymentMethod?.expired"
      :expiresSoon="cardExpiresSoon"
      :totalPrice="totalPrice"
      :isBankTransfer="defaultPaymentMethod?.type === 'WIRE_TRANSFER'"
      :nextPaymentDate="nextPaymentDate"
      :isActivationDateNotToday="isActivationDateNotToday"
    />

    <div v-if="loading" class="tw:mt-6 tw:w-full">
      <div class="tw:animate-pulse tw:rounded tw:bg-gray-200 tw:h-[48px] tw:w-full tw:mb-4" aria-hidden="true" />
    </div>

    <!-- Notifications -->
    <!-- Successfully updated notification -->
    <div
      v-if="!defaultPaymentMethod?.expired && isPaymentUpdated"
      class="tw:bg-green-200 tw:text-sm tw:rounded-lg tw:px-5 tw:py-4"
    >
      {{ $t('paymentMethods.edit.success') }}
    </div>
    <!-- Card soon to expire -->
    <CardToExpireInfo
      v-if="cardExpiresSoon && !isPaymentUpdated"
      :cardExpirationDate="cardExpirationDate"
      type="PLUS"
    />
    <!-- Card expired -->
    <CardExpiredInfo v-if="defaultPaymentMethod?.expired && !isPaymentUpdated" type="PLUS" />

    <!-- Wire transfer -->
    <WireTransferInfo v-if="defaultPaymentMethod?.type === 'WIRE_TRANSFER'" type="PLUS" />

    <div>
      <DefaultPaymentMethodCard
        :defaultPaymentMethod="defaultPaymentMethod"
        @openPaymentPage="openPaymentPage"
        :cardExpiresSoon="cardExpiresSoon"
        type="PLUS"
      />

      <i18n-t
        v-if="!defaultPaymentMethod?.expired"
        keypath="subscription_reactivation_terms_message"
        tag="p"
        class="tw:text-xs tw:pt-4"
      >
        <template #Business_Studio_Plus_terms>
          <BaseLink
            href="/home/terms-and-conditions?app=business-studio-plus"
            target="_blank"
            class="tw:cursor-pointer tw:underline tw:text-[revert] tw:text-gray-800 tw:hover:text-blue-800"
          >
            {{ $t('plus_terms_conditions') }}
          </BaseLink>
        </template>
      </i18n-t>
    </div>

    <div v-show="type === 'YEARLY'" class="tw:mb-4">
      <AddVoucher
        :price="price"
        :voucherData="voucherData"
        :discountAmount="discountAmount"
        :isLoading="loading"
        @applyVoucherCode="applyVoucherCode"
      />
      <div class="tw:flex tw:justify-between tw:mt-6" v-show="voucherData !== null && voucherData?.isValid">
        <p class="tw:font-bold">{{ $t('business_studio_payment_due') }}</p>
        <p class="tw:font-bold tw:text-green-700">
          {{ formatCurrency(totalPrice, { divideBy100: false }) }}
        </p>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import AddVoucher from '@/components/studioPlus/vouchers/AddVoucher.vue'
import DefaultPaymentMethodCard from '@/components/rateConnect/resumeFlow/DefaultPaymentMethodCard.vue'
import CardToExpireInfo from '@/components/rateConnect/resumeFlow/CardToExpireInfo.vue'
import CardExpiredInfo from '@/components/rateConnect/resumeFlow/CardExpiredInfo.vue'
import BaseLink from '@/components/BaseLink.vue'
import StudioPlusReactivationHeader from '@/components/billing/premiumProducts/reactivation/StudioPlusReactivationHeader.vue'
import WireTransferInfo from '@/components/rateConnect/resumeFlow/WireTransferInfo.vue'

import { useStudioPlusReactivation } from './useStudioPlusReactivation'
import { useFormatting } from '@/composables/useFormatting'

const router = useRouter()
const { closeReactivationModal } = useStudioPlusReactivation()
const { formatCurrency } = useFormatting()
const props = defineProps({
  defaultPaymentMethod: Object,
  isPositive: Boolean,
  price: Object,
  nextPaymentDate: String,
  isActivationDateNotToday: Boolean,
  type: String,
  loading: Boolean,
  cardExpiresSoon: Boolean,
  cardExpirationDate: Date,
})

const emit = defineEmits(['applyVoucherCode'])
const applyVoucherCode = code => {
  emit('applyVoucherCode', code)
}
const isPaymentUpdated = computed(() => router.currentRoute.value.query?.isUpdated === 'true')
const totalPrice = computed(() => props.price?.totalPrice / 100)
const voucherData = computed(() => props.price?.itemPriceVoucher)
const discountAmount = computed(() => props.price?.discountAmount ?? 0)

const openPaymentPage = () => {
  router.push({
    name: 'billing-edit-payment',
    query: { isResumeFlow: true, sourceRoute: router.currentRoute.value.name, productType: 'PLUS' },
  })
  closeReactivationModal()
}
</script>
<style scoped>
.payment-info :deep(b):nth-child(2) {
  color: var(--tw-color-green-700);
}
</style>
